import { useState, useEffect } from 'react'
import { ExternalLink } from 'lucide-react'
import { GuestLayout } from '@/layouts/GuestLayout'
import { PoiIcon } from '@/lib/poi-icon'
import { trackEvent } from '@/lib/trackEngagement'
import type { GuestInsideCategory, GuestProperty } from '@/lib/types'

interface Props {
  share_token: string
  property: GuestProperty
  categories: GuestInsideCategory[]
}

function HourTile({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: 'var(--g-surface, #fff)', borderRadius: 14, border: '1px solid var(--g-line)', padding: '14px 14px 16px' }}>
      <div style={{ fontSize: 11, color: 'var(--ink-500)', marginBottom: 4 }}>{label}</div>
      <div className="g-serif" style={{ fontSize: 20, color: 'var(--ink-900)', letterSpacing: '-0.01em' }}>{value}</div>
    </div>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="g-mono" style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--ink-500)', paddingLeft: 4 }}>
      {children}
    </div>
  )
}

function WifiCard({ category, brandColor }: { category: GuestInsideCategory; brandColor: string }) {
  const [copied, setCopied] = useState(false)
  const networkItem  = category.items.find(i => i.title?.toLowerCase().includes('network') || i.title?.toLowerCase().includes('ssid')) ?? category.items[0]
  const passwordItem = category.items.find(i => i.title?.toLowerCase().includes('password') || i.title?.toLowerCase().includes('pass')) ?? category.items[1]

  const ssid     = networkItem?.content ?? networkItem?.title ?? ''
  const password = passwordItem?.content ?? ''

  function copy() {
    navigator.clipboard?.writeText(password).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1400)
  }

  return (
    <div style={{ background: brandColor, color: '#fff', borderRadius: 22, padding: 22, position: 'relative', overflow: 'hidden' }}>
      {/* Background WiFi icon */}
      <div style={{ position: 'absolute', right: -30, top: -30, opacity: 0.15 }}>
        <svg width="160" height="160" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12.55a11 11 0 0114 0M2 8.82a15 15 0 0120 0M8.5 16.43a6 6 0 017 0"/>
          <line x1="12" y1="20" x2="12.01" y2="20"/>
        </svg>
      </div>

      <div className="g-mono" style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', opacity: 0.8, marginBottom: 12 }}>
        WiFi
      </div>

      {ssid && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>Network</div>
          <div className="g-mono" style={{ fontSize: 17, fontWeight: 500 }}>{ssid}</div>
        </div>
      )}

      {password && (
        <div>
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>Password</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="g-mono" style={{ fontSize: 17, fontWeight: 500, flex: 1 }}>{password}</div>
            <button
              onClick={copy}
              className="g-tap"
              style={{
                background: 'rgba(255,255,255,0.2)', border: '0.5px solid rgba(255,255,255,0.3)',
                borderRadius: 999, padding: '6px 12px', color: '#fff', fontSize: 12,
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 500,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
              </svg>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function PhoneRow({ label, number, isLast }: { label: string; number: string; isLast?: boolean }) {
  return (
    <a
      href={`tel:${number}`}
      className="g-tap"
      style={{
        padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14,
        borderBottom: isLast ? 'none' : '1px solid var(--g-line)',
        textDecoration: 'none', color: 'inherit',
      }}
    >
      <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--sand-100, #F4ECE0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-700)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.72 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0122 16.92z"/>
        </svg>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-900)' }}>{label}</div>
        <div className="g-mono" style={{ fontSize: 12, color: 'var(--ink-500)' }}>{number}</div>
      </div>
      <div style={{ padding: '4px 12px', borderRadius: 999, background: 'var(--ink-900, #1F1A14)', color: 'var(--sand-50, #FBF7F1)', fontSize: 12, fontWeight: 500 }}>
        Call
      </div>
    </a>
  )
}

export default function Inside({ share_token, property, categories }: Props) {
  useEffect(() => { if (share_token) trackEvent(share_token, 'inside_view') }, [])
  const credentialCat = categories.find(c => c.render_type === 'credential' && c.items.length > 0)
  const hoursCats     = categories.filter(c => c.render_type === 'hours' && c.items.length > 0)
  const contactCats   = categories.filter(c => c.render_type === 'contact' && c.items.length > 0)
  const otherCats     = categories.filter(c => !['credential', 'hours', 'contact'].includes(c.render_type) && c.items.length > 0)

  const hasContent = categories.some(c => c.items.length > 0)

  return (
    <GuestLayout brandColor={property.brand_color}>
      <div style={{ paddingBottom: 100, background: 'var(--g-bg, #FBF7F1)' }}>

        {/* Header */}
        <div style={{ paddingTop: 64, padding: '64px 24px 8px' }}>
          <div className="g-mono" style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 8 }}>
            Inside · {property.name}
          </div>
          <h1 className="g-serif" style={{ fontSize: 40, lineHeight: 0.98, letterSpacing: '-0.015em', color: 'var(--ink-900)', margin: 0 }}>
            The house, in a few notes.
          </h1>
        </div>

        {!hasContent && (
          <div style={{ padding: '64px 24px', textAlign: 'center', color: 'var(--ink-500)' }}>
            <p style={{ fontSize: 14, margin: 0 }}>No information available yet.</p>
          </div>
        )}

        {/* WiFi card */}
        {credentialCat && (
          <div style={{ padding: '24px 20px 0' }}>
            <WifiCard category={credentialCat} brandColor={property.brand_color} />
          </div>
        )}

        {/* Hours grid */}
        {hoursCats.map(cat => (
          <div key={cat.id} style={{ padding: '24px 20px 0' }}>
            <SectionHeader>{cat.name}</SectionHeader>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
              {cat.items.map(item => (
                <HourTile key={item.id} label={item.title} value={item.content ?? ''} />
              ))}
            </div>
          </div>
        ))}

        {/* Other categories (rules, info, etc.) */}
        {otherCats.map(cat => (
          <div key={cat.id} style={{ padding: '24px 20px 0' }}>
            <SectionHeader>
              {cat.icon
                ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <PoiIcon name={cat.icon} size={14} />
                    {cat.name}
                  </span>
                )
                : cat.name}
            </SectionHeader>
            <div style={{ background: 'var(--g-surface, #fff)', borderRadius: 18, border: '1px solid var(--g-line)', marginTop: 10, overflow: 'hidden' }}>
              {cat.items.map((item, i) => (
                <div
                  key={item.id}
                  className="g-tap"
                  style={{
                    padding: '14px 16px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center',
                    borderBottom: i < cat.items.length - 1 ? '1px solid var(--g-line)' : 'none',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--ink-900)' }}>{item.title}</div>
                    {item.content && <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>{item.content}</div>}
                  </div>
                  {item.cta_url && item.cta_label && (
                    <a href={item.cta_url} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, marginLeft: 8 }}>
                      <ExternalLink size={14} style={{ color: 'var(--ink-500)' }} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Emergency contacts */}
        {contactCats.map(cat => (
          <div key={cat.id} style={{ padding: '24px 20px 0' }}>
            <SectionHeader>{cat.name}</SectionHeader>
            <div style={{ background: 'var(--g-surface, #fff)', borderRadius: 18, border: '1px solid var(--g-line)', marginTop: 10, overflow: 'hidden' }}>
              {cat.items.map((item, i) => (
                <PhoneRow
                  key={item.id}
                  label={item.title}
                  number={item.content ?? item.cta_url ?? ''}
                  isLast={i === cat.items.length - 1}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </GuestLayout>
  )
}
